import { Alert, Box, constants, Stack, Text } from '@redbubble/design-system';
import { defineMessages, IntlShape, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { AnalyticsPromotionCreativeSlot } from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import Image from 'next/legacy/image';
import { InView } from 'react-intersection-observer';
import { trackGA4ViewPromotion } from '../../pages/cart/utils/analytics';
import success from '../../public/icons/success.svg';
import { ConsolidatedPromoBanner } from './ConsolidatedPromoBanner';
import {
  groupDiscountMessages,
  shortenAppliedDiscountText,
} from './groupDiscountMessages';
import styles from './Messages.module.scss';
import {
  Message as MessageInterface,
  MessageSeverity,
  MessageType,
} from './types';
import { unavailableItemMessages } from './unavailableItemMessages';
import { messageSeverityToDesignSystemConstant } from './utils';
export const intlMessages = defineMessages({
  listLabel: {
    defaultMessage: 'Cart messages',
  },
  discountsAppliedHeading: {
    defaultMessage: 'Discounts applied to your cart:',
  },
  discountsAvailableHeading: {
    defaultMessage: 'Available discounts for items in your cart:',
  },
});

export interface MessagesProp {
  messages: MessageInterface[];
  hideAppliedIcons?: boolean;
  // Groups applied discounts and available item-level discounts into two
  // headed, single-line-per-promo banners instead of one banner per message.
  groupDiscounts?: boolean;
}

export const getMsgText = (
  text: string,
  type: MessageType | undefined,
  intl: IntlShape
): string => {
  if (type === MessageType.NonsaleableProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.nonSaleableItem);
  }

  if (type === MessageType.ExpiredProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.expiredItem);
  }

  return text;
};

export const getActionUrl = (
  url: string,
  type: MessageType | undefined,
  locale: string
) => {
  if (type === MessageType.NonsaleableProductRemoval) {
    // TODO[BX-767]: work out how and where we want to normalise urls
    return url.replace(
      '/articles/',
      `/${locale === 'en' ? 'en-us' : locale}/articles/`
    );
  }

  return url;
};

export const Message = ({
  text,
  severity,
  actionUrl,
  showAppliedIcon,
  onScrollInView,
  onClick,
}: {
  text: string;
  severity: MessageSeverity;
  actionUrl?: string;
  showAppliedIcon?: boolean;
  onScrollInView?: () => void;
  onClick?: () => void;
}) => {
  const content = (
    <Stack direction={constants.HORIZONTAL} spacing="m">
      {showAppliedIcon && (
        <Image
          alt=""
          layout="fixed"
          src={success}
          width={22}
          height={24}
          aria-hidden
        />
      )}
      <Text>{text}</Text>
    </Stack>
  );

  const intent = messageSeverityToDesignSystemConstant[severity];

  return (
    <InView onChange={onScrollInView} as="li" triggerOnce>
      <Alert intent={intent} label={false}>
        {actionUrl ? (
          <a href={actionUrl} className={styles.link} onClick={onClick}>
            {content}
          </a>
        ) : (
          content
        )}
      </Alert>
    </InView>
  );
};

export const Messages = ({
  messages,
  hideAppliedIcons,
  groupDiscounts,
}: MessagesProp) => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const intl = useIntl();

  if (messages.length === 0) {
    return <div />;
  }

  const handleInViewTracking = (message: MessageInterface) => {
    if (message.text && message.type === MessageType.DiscountSitewide) {
      trackGA4ViewPromotion({
        promotionName: message.text,
        creativeSlot: AnalyticsPromotionCreativeSlot.CartMessages,
      });
    }
  };

  const getPromoKey = (message: MessageInterface) =>
    `${message.text}_${message.type}_${message.itemId}`;

  const getPromoActionUrl = (message: MessageInterface) =>
    message.actionUrl
      ? getActionUrl(message.actionUrl, message.type, locale)
      : undefined;

  const renderMessage = (message: MessageInterface) => (
    <Message
      key={getPromoKey(message)}
      actionUrl={getPromoActionUrl(message)}
      severity={message.severity}
      text={getMsgText(message.text || '', message.type, intl)}
      showAppliedIcon={message.applied && !hideAppliedIcons}
      onScrollInView={() => handleInViewTracking(message)}
    />
  );

  const renderGroupedContent = () => {
    const { othersBefore, applied, availableItemDiscounts, othersAfter } =
      groupDiscountMessages(messages);

    return (
      <>
        {othersBefore.map(renderMessage)}
        <ConsolidatedPromoBanner<MessageInterface>
          element="li"
          intent={constants.SUCCESS}
          heading={intl.formatMessage(intlMessages.discountsAppliedHeading)}
          messages={applied}
          showIcon={!hideAppliedIcons}
          getKey={getPromoKey}
          getLineText={(message) =>
            shortenAppliedDiscountText(
              getMsgText(message.text || '', message.type, intl)
            )
          }
          getActionUrl={getPromoActionUrl}
          onLineInView={handleInViewTracking}
        />
        <ConsolidatedPromoBanner<MessageInterface>
          element="li"
          intent={constants.INFORMATION}
          heading={intl.formatMessage(intlMessages.discountsAvailableHeading)}
          messages={availableItemDiscounts}
          getKey={getPromoKey}
          getLineText={(message) =>
            getMsgText(message.text || '', message.type, intl)
          }
          getActionUrl={getPromoActionUrl}
          onLineInView={handleInViewTracking}
        />
        {othersAfter.map(renderMessage)}
      </>
    );
  };

  const content = groupDiscounts
    ? renderGroupedContent()
    : messages.map(renderMessage);

  return (
    // NOTE: it would've been better to use a <Stack /> here but I couldn't pass
    // in a classname to get rid of the default <ul> styling
    <Box
      className={styles.wrapper}
      element="ul"
      aria-label={intl.formatMessage(intlMessages.listLabel)}
    >
      {content}
    </Box>
  );
};
